/**
 * What needs the person, in order: sessions waiting on them, agents that look stuck, failed
 * pipeline items (work rejected more than once as one row), machines that stopped reporting,
 * and dirty worktrees nobody has touched for a day. Pure, so it is tested.
 */
import type { Checkout, Pipeline, PipelineItem, Session } from '@orgus/protocol';
import { DIRTY_WORKTREE_NEEDS_YOU_MS, LANDING_FATE, asksStateOf, machineLiveness, needsYou, pipelineKey } from '@orgus/protocol';
import type { ApprovalsState, LandingState, Risk } from '@orgus/protocol';
import type { Entities } from '../../lib/store.ts';
import { repeatedIds, repeatedRejectionNeeds, stuckAgentNeeds } from './needsYouTimeline.ts';
import { plainText } from '../../lib/plainText.ts';
import { landingNeeds } from '../landing/needs.ts';
import { missionsSlice } from '../missions/model.ts';
import { shownTitle } from '../sessions/shownTitle.ts';

export type NeedKind = 'session' | 'asked' | 'agent' | 'landing' | 'pipeline' | 'machine' | 'worktree';

/**
 * What a row is about, when an Orgus Watch incident can describe the same thing (one session, a
 * worktree folder, a branch's landing, a machine), so Needs you says it once (watch/needs.ts).
 */
export interface NeedSubject {
  kind: 'session' | 'worktree' | 'branch' | 'machine';
  id: string;
}

export interface NeedItem {
  key: string;
  kind: NeedKind;
  title: string;
  detail: string;
  /** Machine (and project) the item lives on. */
  where: string;
  /** The project, for routing and assignment (module teams); null for machine-wide items. */
  projectId?: string | null;
  /** When it happened; null when there is no meaningful time (a worktree nobody worked in). */
  at: number | null;
  /** What one click does: open the session drawer, answer a request in place (the answer sheet), or go to a path. */
  to: { session: string } | { approval: string } | { path: string };
  /** The verb on the row. */
  verb: string;
  /** A pending request's deadline and risk, for the one order inside its group (ADR 0072). */
  order?: { deadline?: number; risk?: Risk };
  /** What it is about, for the rows an incident can repeat; absent on decisions. */
  subject?: NeedSubject;
}

/** Failed pipeline items count while they are this recent. */
export const FAILED_ITEM_WINDOW_MS = 6 * 60 * 60_000;

/** A title the adapter made up because it does not know the real one ("Submission 1792..."). */
export const isPlaceholderTitle = (t: string): boolean => /^Submission \S+$/.test(t);

/**
 * The failures in a pipeline that still need someone: recent, newest per title, and not
 * already resubmitted (the same title is back in flight, or passed or failed again later).
 * Newest first.
 */
export function openFailures(p: Pipeline, now: number): PipelineItem[] {
  // A failure whose branch landed since is settled (its fate tag says so, ORGU-83).
  const recent = p.recent.filter((i) => i.state === 'failed' && !i.tags?.includes(LANDING_FATE.landedSince) && !i.tags?.includes(LANDING_FATE.gone) && i.endedAt != null && i.endedAt >= now - FAILED_ITEM_WINDOW_MS);
  const inFlight = new Set(p.items.map((i) => i.title));
  const latestEnd = new Map<string, number>();
  for (const i of p.recent) if (i.endedAt != null) latestEnd.set(i.title, Math.max(latestEnd.get(i.title) ?? 0, i.endedAt));
  const seen = new Set<string>();
  return recent
    .sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
    .filter((i) => {
      if (isPlaceholderTitle(i.title)) return true;
      if (seen.has(i.title)) return false;
      seen.add(i.title);
      return !inFlight.has(i.title) && (latestEnd.get(i.title) ?? 0) <= (i.endedAt ?? 0);
    });
}

const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();

/** Sessions that ran in a checkout: by id, or by a cwd inside its path. */
export function sessionsIn(c: Checkout, sessions: Session[]): Session[] {
  return workIndex(sessions).sessionsIn(c);
}

/** What `sessionsIn` and `lanesIn` answer for many checkouts, from one pass over the sessions. */
export interface WorkIndex {
  sessionsIn(c: Checkout): Session[];
  lanesIn(c: Checkout): { running: boolean; last: number };
}

/**
 * Sessions and workflow agents by machine, their paths normalised once. Asking each checkout
 * against every session (and normalising every path again each time) took about 300 ms per
 * live update with 10,000 sessions and 1,200 checkouts (21 Oct 2026); a machine holds a few
 * dozen.
 */
export function workIndex(sessions: readonly Session[]): WorkIndex {
  const byMachine = new Map<string, { s: Session; cwd: string }[]>();
  const agents = new Map<string, { path: string; running: boolean; at: number }[]>();
  for (const s of sessions) {
    let list = byMachine.get(s.machineId);
    if (!list) byMachine.set(s.machineId, (list = []));
    list.push({ s, cwd: norm(s.cwd) });
    for (const w of s.workflows ?? []) {
      for (const a of w.agents ?? []) {
        if (!a.worktree) continue;
        let lanes = agents.get(s.machineId);
        if (!lanes) agents.set(s.machineId, (lanes = []));
        lanes.push({ path: norm(a.worktree), running: a.status === 'running', at: a.endedAt ?? a.startedAt });
      }
    }
  }
  return {
    sessionsIn(c) {
      const root = norm(c.path);
      const out: Session[] = [];
      for (const { s, cwd } of byMachine.get(c.machineId) ?? []) if (s.checkoutId === c.id || cwd === root || cwd.startsWith(`${root}/`)) out.push(s);
      return out;
    },
    lanesIn(c) {
      const root = norm(c.path);
      let running = false;
      let last = 0;
      for (const a of agents.get(c.machineId) ?? []) {
        if (a.path !== root && !a.path.startsWith(`${root}/`)) continue;
        if (a.running) running = true;
        last = Math.max(last, a.at);
      }
      return { running, last };
    },
  };
}

/**
 * The newest session that worked on a branch: a checkout on the branch, then the sessions in it.
 * Ended ones count: waking an ended session resumes it with the failure (ADR 0055, ORGU-83).
 */
export function sessionOnBranch(branch: string, checkouts: Checkout[], sessions: Session[] | WorkIndex): Session | null {
  const index = Array.isArray(sessions) ? workIndex(sessions) : (sessions as WorkIndex);
  let best: Session | null = null;
  for (const c of checkouts) {
    if (c.branch !== branch) continue;
    for (const s of index.sessionsIn(c)) if (!best || s.lastActivity > best.lastActivity) best = s;
  }
  return best;
}

/** The path that opens Start a mission prefilled for a stuck landing (Missions.tsx reads it). */
export function missionStartPath(projectId: string, branch: string, detail: string): string {
  const q = new URLSearchParams({
    start: '1',
    project: projectId,
    title: `Land ${branch}`,
    goal: `The landing of ${branch} failed and nobody has it${detail ? ` (${detail})` : ''}. Fix it in its worktree, commit, and land it with orgus land --submit.`,
  });
  return `/missions?${q.toString()}`;
}

/**
 * Workflow agents that worked in a checkout (a lane's own worktree): whether one is running
 * there now, and when the last one ended. They are not sessions, but their worktrees are not
 * abandoned while they work.
 */
export function lanesIn(c: Checkout, sessions: Session[]): { running: boolean; last: number } {
  return workIndex(sessions).lanesIn(c);
}

export function computeNeedsYou(e: Pick<Entities, 'sessions' | 'pipelines' | 'machines' | 'checkouts' | 'projects'> & { ext?: Record<string, unknown> }, now: number): NeedItem[] {
  // Hub-side work (the landing queue) carries machineId 'hub': name no machine rather than "Unknown machine".
  const machineName = (id: string) => e.machines[id]?.name ?? null;
  const projectName = (id: string | null) => (id ? (e.projects[id]?.name ?? null) : null);
  const where = (machineId: string, projectId: string | null) => [projectName(projectId), machineName(machineId)].filter(Boolean).join(' on ');
  const sessions = Object.values(e.sessions);
  // Built on first use: most updates have no stuck landing and no dirty worktree to ask about.
  let index: WorkIndex | null = null;
  const work = () => (index ??= workIndex(sessions));
  const missionTitles = new Map((missionsSlice(e.ext ?? {})?.missions ?? []).map((m) => [m.id, m.title]));
  const out: NeedItem[] = [];

  // The one selector (ADR 0056): blocked sessions read Needs you, asked ones Asked; an asked
  // question older than a day, or dismissed or snoozed, is not here. Requests are added by
  // withApprovals and withBlocked.
  const approvals = e.ext?.approvals as Partial<ApprovalsState> | undefined;
  const ny = needsYou({ sessions, pending: approvals?.pending ?? [], setAside: asksStateOf(e.ext).setAside, now });
  for (const i of [...ny.blocked, ...ny.asked]) {
    const s = i.session;
    if (!s) continue;
    const asked = i.level === 'asked';
    out.push({
      key: `s:${s.id}`,
      kind: asked ? 'asked' : 'session',
      title: shownTitle(s, (id) => missionTitles.get(id)).title,
      detail: plainText(i.reason) || 'Claude is waiting for your answer.',
      where: where(s.machineId, s.projectId),
      projectId: s.projectId,
      at: i.at,
      to: { session: s.id },
      verb: 'Answer',
    });
  }

  out.push(...stuckAgentNeeds(e, now));
  out.push(...repeatedRejectionNeeds(e, now, FAILED_ITEM_WINDOW_MS));
  const repeated = repeatedIds(e.pipelines, now, FAILED_ITEM_WINDOW_MS);

  const allCheckouts = Object.values(e.checkouts);
  for (const p of Object.values(e.pipelines)) {
    // Work rejected more than once already has its own row (repeatedRejectionNeeds).
    const open = openFailures(p, now).filter((i) => !repeated.has(i.id));
    const key = pipelineKey(p);
    // A stuck failure (its fate tag, ORGU-83): nobody has it, so its row carries the way out:
    // wake the branch's session (a wake resumes an ended one), or start a mission on it.
    const stuck = open.filter((i) => i.tags?.includes(LANDING_FATE.stuck));
    for (const i of stuck) {
      const s = sessionOnBranch(i.title, allCheckouts, work());
      out.push({
        key: `p:${key}:${i.id}`,
        kind: 'pipeline',
        title: i.title,
        detail: `${i.detail ? `${i.detail}. ` : ''}${s ? 'Wake its session to fix and resubmit it.' : 'No session to wake: start a mission on it.'}`,
        where: where(p.machineId, p.projectId),
        projectId: p.projectId,
        at: i.endedAt ?? p.updatedAt,
        to: s ? { session: s.id } : { path: missionStartPath(p.projectId, i.title, i.detail ?? '') },
        verb: s ? 'Wake' : 'Start a mission',
        subject: { kind: 'branch', id: i.title },
      });
    }
    const failures = open.filter((i) => !stuck.includes(i));
    if (failures.length === 1) {
      const i = failures[0]!;
      const stage = p.stages.find((st) => st.id === i.stage)?.title;
      out.push({
        key: `p:${key}:${i.id}`,
        kind: 'pipeline',
        title: i.title,
        detail: [stage ? `Failed in ${stage.toLowerCase()}` : 'Failed', i.detail].filter(Boolean).join(': '),
        where: where(p.machineId, p.projectId),
        projectId: p.projectId,
        at: i.endedAt ?? p.updatedAt,
        to: { path: `/pipelines?pipeline=${encodeURIComponent(key)}&item=${encodeURIComponent(i.id)}` },
        verb: 'See why',
      });
    } else if (failures.length > 1) {
      // Several failures in one pipeline are one row, so they never drown everything else.
      const named = failures.filter((i) => !isPlaceholderTitle(i.title)).map((i) => i.title);
      const shown = named.slice(0, 2).map((t) => `"${t}"`);
      const rest = failures.length - shown.length;
      out.push({
        key: `p:${key}:failures`,
        kind: 'pipeline',
        title: `${failures.length} items failed in the ${p.title.toLowerCase()}`,
        detail: shown.length ? `${shown.join(', ')}${rest > 0 ? ` and ${rest} more` : ''}` : 'None has been submitted again since.',
        where: where(p.machineId, p.projectId),
        at: failures[0]!.endedAt ?? p.updatedAt,
        to: { path: `/pipelines?pipeline=${encodeURIComponent(key)}` },
        verb: 'Review',
      });
    }
  }

  out.push(...landingNeeds(e.ext?.landing as LandingState | undefined, projectName, now));

  for (const m of Object.values(e.machines)) {
    const l = machineLiveness(m, now);
    if (l === 'online') continue;
    out.push({
      key: `m:${m.id}`,
      kind: 'machine',
      title: l === 'offline' ? `${m.name} is offline` : `${m.name} stopped reporting`,
      detail: 'Its sessions, checkouts and pipelines are not updating.',
      where: m.hostname,
      at: m.lastSeen,
      to: { path: `/machines?machine=${encodeURIComponent(m.id)}` },
      verb: 'Check',
      subject: { kind: 'machine', id: m.id },
    });
  }

  for (const c of Object.values(e.checkouts)) {
    if (c.kind !== 'worktree' || c.dirty <= 0) continue;
    const inIt = work().sessionsIn(c);
    const lanes = work().lanesIn(c);
    if (inIt.some((s) => s.status !== 'ended') || lanes.running) continue;
    const last = Math.max(0, lanes.last, ...inIt.map((s) => s.lastActivity));
    if (last && now - last < DIRTY_WORKTREE_NEEDS_YOU_MS) continue;
    out.push({
      key: `w:${c.id}`,
      kind: 'worktree',
      title: `${c.branch ?? 'Detached worktree'} has ${c.dirty} uncommitted ${c.dirty === 1 ? 'change' : 'changes'}`,
      detail: last ? 'No session or agent has worked in it for over a day.' : 'No session or agent has worked in it recently.',
      where: where(c.machineId, c.projectId),
      projectId: c.projectId,
      at: last || null,
      to: { path: `/projects?project=${encodeURIComponent(c.projectId)}&checkout=${encodeURIComponent(c.id)}` },
      verb: 'Review',
      subject: { kind: 'worktree', id: c.path },
    });
  }

  const rank: Record<NeedKind, number> = { session: 0, agent: 1, asked: 2, landing: 3, pipeline: 3, machine: 4, worktree: 5 };
  return out.sort((a, b) => rank[a.kind] - rank[b.kind] || (b.at ?? 0) - (a.at ?? 0));
}
